import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

const Hero = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  // Defer the hero video until the browser is idle, so it never competes with
  // the poster (the LCP element) or with first paint.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    const start = () => {
      if (cancelled || video.src) return;
      // A full-bleed hero video becomes the LCP element the moment it paints,
      // so LCP tracks its download. Phones get the small encode, not the 684KB one.
      //
      // This keys off screen.width rather than innerWidth or a media query:
      // page zoom shrinks the CSS viewport, so at 250% zoom a laptop reports
      // ~576px and would be served the phone encode, then upscale it across a
      // large physical display. screen.width is zoom-invariant.
      const screenWidth = window.screen?.width || window.innerWidth;
      const wantsSmall = screenWidth <= 767 || navigator.connection?.saveData;
      video.src = wantsSmall ? '/hero-mobile.mp4' : '/hero.mp4';
      video.load();
      video.play().catch(() => {
        /* autoplay blocked - the poster stays, which is a fine fallback */
      });
    };

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 3000 })
      : setTimeout(start, 1500);

    return () => {
      cancelled = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, []);

  // Parallax Effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={containerRef} className="bg-white pt-24 pb-12 px-4 md:px-8 lg:px-12 w-full">
      
      {/*
        min-h matters at high browser zoom: zoom shrinks the CSS viewport, so a
        pure vh height collapses (225px at 300%) below the height of the centred
        copy. A centre-aligned flex column overflows in both directions, and this
        box clips, so the headline would slide up out of frame. The floor keeps
        the copy inside the frame; phones are unaffected because 75vh is taller.
      */}
      <div className="relative w-full h-[75vh] min-h-[500px] md:h-[85vh] overflow-hidden bg-gray-100">
        
        {/* 1. BACKGROUND MEDIA */}
        <motion.div 
          style={{ y }}
          className="absolute inset-0 w-full h-[120%]"
        >
          {/* Poster is the LCP element - preloaded from index.html */}
          <img
            src="/hero-poster-1280.webp"
            srcSet="/hero-poster-800.webp 800w, /hero-poster-1280.webp 1280w"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="none"
            onCanPlay={() => setVideoReady(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" /> 
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        </motion.div>

        {/* 2. CENTERED TEXT CONTENT */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10 px-4 py-6">
          
          {/* Headline - "WEAR THE DREAM" */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="relative"
          >
            <div className="drop-shadow-lg">
              {/* Reduced mobile size (text-4xl) to ensure 3 words fit on one line or wrap nicely */}
              <h1 className="text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-serif italic tracking-tighter leading-none mb-2 mix-blend-overlay">
                TROPICAL <span className="not-italic font-light">NIGHTS</span>
              </h1>
              <h1 className="absolute top-0 left-0 w-full text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-serif italic tracking-tighter leading-none mb-2 opacity-95">
                TROPICAL <span className="not-italic font-light">NIGHTS</span>
              </h1>
            </div>
          </motion.div>

          {/* Separator */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.8, ease: "circOut" }}
            className="w-16 h-[2px] bg-white my-6 md:my-8 drop-shadow-md"
          />

          {/* Subtitle - The Product Name */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: "easeOut" }}
            className="text-white text-xs md:text-sm uppercase tracking-[0.3em] font-medium mb-10 drop-shadow-xl"
          >
            Introducing Tropical Nights 
          </motion.p>

          {/* Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: "easeOut" }}
          >
            <Link to="/shop">
              <button className="group relative px-10 py-4 overflow-hidden rounded-sm bg-black/30 backdrop-blur-sm border border-white transition-colors">
                <span className="absolute inset-0 w-full h-full bg-white transform -translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0"></span>
                <span className="relative z-10 text-[10px] uppercase tracking-[0.2em] text-white font-semibold group-hover:text-black transition-colors duration-300">
                  Shop Now
                </span>
              </button>
            </Link>
          </motion.div>
        </div>

        {/* 3. SCROLL INDICATOR */}
        <motion.div 
          style={{ opacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        >
          <span className="text-[9px] uppercase tracking-widest text-white font-medium drop-shadow-md">Scroll</span>
          <div className="w-[1px] h-8 bg-white/50 overflow-hidden">
            <div className="w-full h-full bg-white animate-scrolldown"></div>
          </div>
        </motion.div>

      </div>
      
      <style>{`
        @keyframes scrolldown {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        .animate-scrolldown {
          animation: scrolldown 2s cubic-bezier(0.77, 0, 0.175, 1) infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;